'use client';

import { useTranslations } from 'next-intl';
import type { ContactWidgetResolved } from './types';
import { useContactSubmit } from './useContactSubmit';
import { Button } from '@/components/Button';
import { ContactForm } from '@/components/form/ContactForm';
import { Icon } from '@/components/Icon';
import { SubmittedCard } from '@/components/SubmittedCard';
import { createFormSchemas } from '@/lib/form/schemas';
import type { ExtraField } from '@/lib/form/types';
import { hasCenterName, resolveCenterName } from '@/lib/location/centerName';
import { useRenderContext } from '@/lib/render/RenderContextProvider';
import { RichText } from '@/lib/render/RichText';

/*
 * The form measure (70.14-Widget-Width § 5): narrower than the page on
 * purpose, because a form is read and filled rather than scanned — see
 * 10.03.04.01, which treats the form container width as its own property.
 * Only the measure; the gutter comes from the SlotRenderer wrapper.
 */
const containerClassName = 'mx-auto max-w-4xl pt-4';

export function ContactWidgetComponent({
  resolved,
}: {
  resolved: ContactWidgetResolved;
}) {
  const { entities } = useRenderContext();
  const location = entities.location;

  const tForm = useTranslations('form');
  const tValidation = useTranslations('validation');
  const tCommon = useTranslations('common');

  const { submitted, submittedUser, hasError, handleSubmit } = useContactSubmit(
    resolved.formType,
    resolved.id,
    resolved.pageDocumentId,
    resolved.pageLocale,
    location?.slug,
    resolved.submitTracking,
    resolved.tracking,
  );

  const schemas = createFormSchemas(tValidation);

  const extraFields: ExtraField[] = [
    {
      name: 'consent',
      component: 'Checkbox',
      validator: schemas.consentSchema,
      props: { children: tForm('consentLabel') },
    },
  ];

  const isVoucher = resolved.formType === 'voucher';

  if (submitted && submittedUser) {
    const zipCity =
      location?.zipCode && location.cityName
        ? `${location.zipCode} ${location.cityName}`
        : null;

    return (
      <div className={containerClassName}>
        <SubmittedCard>
          <h3 className="mb-4 font-medium">
            {tForm('thankYouName', {
              salutation: tForm(submittedUser.salutation),
              name: submittedUser.lastname,
            })}
          </h3>

          {!!resolved.confirmationBody?.length && (
            <div className="mb-10 text-gray-500">
              <RichText nodes={resolved.confirmationBody} />
            </div>
          )}

          {hasCenterName(location) && (
            <dl className="mx-auto inline-block text-left text-sm">
              <div className="flex items-start gap-4">
                <dt className="mt-1 text-gray-500">
                  {/* The pin is the only thing naming this term. */}
                  <Icon
                    src="/icons/map-pin.svg"
                    size={20}
                    label={tCommon('summaryLocation')}
                  />
                </dt>
                <dd>
                  <div>{resolveCenterName(location)}</div>
                  {location?.street && <div>{location.street}</div>}
                  {zipCity && <div>{zipCity}</div>}
                </dd>
              </div>
            </dl>
          )}
        </SubmittedCard>
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      <ContactForm
        onSubmit={handleSubmit}
        extraFields={extraFields}
        className="mx-auto"
      >
        {({ isSubmitting }) => (
          <div className="mt-6 flex flex-col items-center">
            <Button
              size="lg"
              type="submit"
              disabled={isSubmitting}
              className="w-full text-sm md:w-sm"
            >
              {isSubmitting
                ? '…'
                : isVoucher
                  ? tForm('submitButtonVoucher')
                  : tForm('submitButton')}
            </Button>
            {hasError && (
              <p className="mt-4 text-center text-red-600" role="alert">
                {tForm('submissionError')}
              </p>
            )}
          </div>
        )}
      </ContactForm>
    </div>
  );
}
